// src/pages/LynMap.jsx
// Learn-Your-Notes "Note Trail" — the overworld for the falling-note game.
// Each building lights up as you finish its level, shows your best stars, and
// finishing one unlocks the next. The "Town Magic" meter fills as you restore it.
// Route: /lyn-map  (separate from the song road map at /treble-town-map)

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LEVELS, LEVEL_ORDER } from "../data/lynLevels";
import { loadProgress } from "../utils/lynProgress";

export default function LynMap() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState({});

  const refresh = useCallback(() => setProgress(loadProgress()), []);
  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);

  const done = (id) => !!(progress[id] && progress[id].completed);
  const starsOf = (id) => (progress[id] && progress[id].stars) || 0;
  const unlocked = (id) => {
    const idx = LEVEL_ORDER.indexOf(id);
    return idx <= 0 ? true : done(LEVEL_ORDER[idx - 1]);
  };

  const playable = LEVEL_ORDER.filter((id) => LEVELS[id] && !LEVELS[id].comingSoon);
  const townPct = playable.length
    ? Math.round((playable.reduce((s, id) => s + starsOf(id), 0) / (playable.length * 3)) * 100)
    : 0;

  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "linear-gradient(180deg,#bae6fd 0%,#e0f2fe 30%,#ecfdf5 70%,#d9f99d 100%)",
        fontFamily: "'Nunito', sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "16px 12px 60px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');
        @keyframes lmFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes lmGlow { 0%,100%{box-shadow:0 10px 26px rgba(0,0,0,0.12)} 50%{box-shadow:0 12px 34px rgba(56,189,248,0.5)} }
        button { -webkit-tap-highlight-color: transparent; }
      `}</style>

      <div style={{ position: "absolute", top: 60, left: "8%", fontSize: "2.4rem", opacity: 0.85, animation: "lmFloat 6s ease-in-out infinite" }}>☁️</div>
      <div style={{ position: "absolute", top: 120, right: "10%", fontSize: "3rem", opacity: 0.8, animation: "lmFloat 7s ease-in-out infinite" }}>☁️</div>

      {/* Header */}
      <div style={{ width: "100%", maxWidth: 640, display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 2 }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            background: "rgba(255,255,255,0.9)",
            border: "1px solid #E2E8F0",
            borderRadius: 12,
            padding: "8px 12px",
            fontWeight: 800,
            color: "#475569",
            cursor: "pointer",
            fontSize: "0.82rem",
          }}
        >
          ← Back
        </button>
        <div style={{ fontFamily: "'Fredoka One', cursive", color: "#0c4a6e", fontSize: "clamp(1.15rem,4vw,1.6rem)" }}>
          Note Trail 🎵
        </div>
        <div style={{ width: 64 }} />
      </div>

      <div style={{ color: "#0369A1", fontWeight: 800, fontSize: "0.86rem", marginTop: 4, textAlign: "center", zIndex: 2 }}>
        Learn your notes to restore Treble Town's magic! 🐼🦉
      </div>

      {/* Town magic meter */}
      <div style={{ position: "relative", width: "100%", maxWidth: 420, margin: "14px auto 6px", zIndex: 2 }}>
        <div
          style={{
            width: "100%",
            height: 30,
            borderRadius: 999,
            background: "linear-gradient(180deg,#1e293b,#0f172a)",
            padding: 3,
            boxShadow: "inset 0 2px 6px rgba(0,0,0,0.5)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${townPct}%`,
              height: "100%",
              borderRadius: 999,
              background: "linear-gradient(180deg,#a7f3d0,#34d399 55%,#059669)",
              boxShadow: "inset 0 2px 3px rgba(255,255,255,0.6), 0 0 14px rgba(52,211,153,0.6)",
              transition: "width .5s cubic-bezier(.34,1.56,.64,1)",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'Fredoka One', cursive",
              fontSize: "0.82rem",
              color: "#fff",
              textShadow: "0 1px 2px rgba(0,0,0,0.5)",
            }}
          >
            Town Magic {townPct}%
          </div>
        </div>
      </div>

      {/* Path of buildings */}
      <div style={{ position: "relative", width: "100%", maxWidth: 460, marginTop: 12, zIndex: 2 }}>
        {LEVEL_ORDER.map((id, i) => {
          const lv = LEVELS[id];
          if (!lv) return null;
          const isDone = done(id);
          const isOpen = unlocked(id) && !lv.comingSoon;
          const locked = !unlocked(id) || lv.comingSoon;
          const st = starsOf(id);
          const last = i === LEVEL_ORDER.length - 1;

          return (
            <div key={id} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <button
                type="button"
                disabled={!isOpen}
                onClick={() => isOpen && navigate(`/lyn-game/${id}`)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  textAlign: "left",
                  background: locked ? "rgba(241,245,249,0.92)" : "rgba(255,255,255,0.97)",
                  border: `2px solid ${isDone ? lv.accent : isOpen ? "#bae6fd" : "#E2E8F0"}`,
                  borderRadius: 22,
                  padding: "16px 18px",
                  cursor: isOpen ? "pointer" : "not-allowed",
                  boxShadow: isOpen && !isDone ? "0 10px 26px rgba(56,189,248,0.22)" : "0 8px 20px rgba(15,23,42,0.08)",
                  animation: isOpen && !isDone ? "lmGlow 2.4s ease-in-out infinite" : "none",
                  opacity: locked ? 0.7 : 1,
                }}
              >
                {/* building icon */}
                <div
                  style={{
                    flexShrink: 0,
                    width: 64,
                    height: 64,
                    borderRadius: 18,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "2rem",
                    background: locked ? "#E2E8F0" : `${lv.accent}22`,
                    border: `2px solid ${locked ? "#CBD5E1" : lv.accent}`,
                  }}
                >
                  {lv.comingSoon || !unlocked(id) ? "🔒" : lv.icon}
                </div>

                {/* info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: "1.05rem", color: "#0f172a" }}>
                    {lv.building}
                  </div>
                  <div style={{ fontWeight: 700, color: "#64748B", fontSize: "0.82rem", marginTop: 1 }}>
                    {lv.comingSoon ? "Coming soon" : lv.blurb}
                  </div>
                  {!lv.comingSoon && (
                    <div style={{ marginTop: 6, display: "flex", gap: 3, fontSize: "1.05rem", lineHeight: 1 }}>
                      {[0, 1, 2].map((s) => (
                        <span key={s} style={{ filter: s < st ? "none" : "grayscale(1) opacity(0.3)" }}>⭐</span>
                      ))}
                    </div>
                  )}
                </div>

                {/* right action chip */}
                <div style={{ flexShrink: 0 }}>
                  {lv.comingSoon ? (
                    <span style={{ fontSize: "0.7rem", fontWeight: 800, color: "#94A3B8" }}>SOON</span>
                  ) : isDone ? (
                    <span style={{ fontFamily: "'Fredoka One', cursive", color: lv.accent, fontSize: "0.78rem" }}>Replay ↺</span>
                  ) : isOpen ? (
                    <span
                      style={{
                        fontFamily: "'Fredoka One', cursive",
                        color: "#fff",
                        background: "linear-gradient(180deg,#22c55e,#16a34a)",
                        padding: "8px 14px",
                        borderRadius: 999,
                        fontSize: "0.82rem",
                        boxShadow: "0 4px 12px rgba(22,163,74,0.4)",
                      }}
                    >
                      Play ▶
                    </span>
                  ) : (
                    <span style={{ fontSize: "1.1rem", opacity: 0.5 }}>🔒</span>
                  )}
                </div>
              </button>

              {!last && (
                <div style={{ width: 0, height: 26, borderLeft: "3px dashed #93c5fd", margin: "4px 0" }} />
              )}
            </div>
          );
        })}
      </div>

      {/* hills */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 70, background: "radial-gradient(60% 120% at 20% 0%, #86efac 0%, transparent 60%), radial-gradient(60% 120% at 80% 0%, #6ee7b7 0%, transparent 60%)", opacity: 0.7, pointerEvents: "none" }} />
    </div>
  );
}